import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isFunction } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { Box, Skeleton } from '../../../../../1-primative';
import {
  Table, TableRow, TableCell, TableHeadCell, Checkbox, Pagination,
} from '../../../../../2-component';
import { Identifiable, RebalanceReportColumn } from '../sharedTypes';
import useTableSort from './useTableSort';
import useSelectionModel from './useSelectionModel';
import useTableColumnDragDrop from './useTableColumnDragDrop';

type TableProps<T> = {
  data: T[];
  columns: RebalanceReportColumn<T>[];
  loading: boolean;
  initialPage: number;
  linesPerPage: number;
  totalLines: number;
  checkbox: boolean;
  pagination: boolean;
  testIdPrefix?: string;
  footer?: boolean | ((data: T[])=> ReactNode);
  onPage?: (page: number, itemsPerPage: number) => void;
  onSort?: (column: keyof T | null, direction: 'asc' | 'desc' | undefined) => void;
  onSelect?: (data: T[]) => void;
  onTableRowClick?: (row: T) => void;
};

const RebalanceReportTable = <T extends Identifiable>({
  data, columns: initialColumns, loading, initialPage, linesPerPage, totalLines, checkbox, pagination, footer, onPage, onSort, onSelect, onTableRowClick, testIdPrefix,
}: TableProps<T>) => {
  const { t } = useTranslation(['rebalanceReportV2']);
  const [selected] = useState<T[]>([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(linesPerPage);

  const {
    toggle,
    isSelected,
    clear,
    setSelection: setSelectionWithToggle,
  } = useSelectionModel<T>([], true, (item: T) => item.id);

  const {
    sortColumn,
    sortDirection,
    handleSort,
  } = useTableSort<T>(initialColumns);

  const {
    columns,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useTableColumnDragDrop<T>(initialColumns);

  const visibleColumns = columns.filter((column) => column.visible !== false);
  const partitionedColumns = {
    leftColumns: visibleColumns.filter((column) => column.pinned === 'left'),
    normalColumns: visibleColumns.filter((column) => !column.pinned),
    rightColumns: visibleColumns.filter((column) => column.pinned === 'right'),
  };

  useEffect(() => {
    if (onSort) onSort(sortColumn, sortDirection);
  }, [sortColumn, sortDirection, onSort]);

  useEffect(() => {
    if (onSelect) { onSelect(selected); }
  }, [selected, onSelect]);

  useEffect(() => {
    if (onPage) {
      onPage(currentPage, itemsPerPage);
    }
  }, [currentPage, itemsPerPage, onPage]);

  const getRowKey = (row: T, index: string | number) => `${row.id}-${index}`;
  const setTestIdPrefix = () => (isEmpty(testIdPrefix) ? '' : `${testIdPrefix}-`);

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          <TableRow testId={`${setTestIdPrefix()}table-row-titles`}>
            { checkbox && <TableHeadCell padding='none' testId={`${setTestIdPrefix()}table-head-select-all`}>
              <Box display='flex' alignItems='center' justifyContent='center'>
                <Checkbox
                  testId={`${setTestIdPrefix()}table-checkbox-select-all`}
                  indeterminate={selected.length > 0 && selected.length < data.length}
                  checked={selected.length === data.length && data.length > 0}
                  onClick={(event) => { event?.stopPropagation(); }}
                  onChange={(checked: boolean) => {
                    if (checked) {
                      setSelectionWithToggle(data);
                    } else {
                      clear();
                    }
                  }}
                  customStyle={{
                    marginLeft: '5px',
                  }}
                />
              </Box>
            </TableHeadCell> }
            {partitionedColumns.leftColumns.map((column) => (
              <TableHeadCell
                draggable
                onDragStart={(e) => handleDragStart(e, column.accessor as keyof T)}
                onDragOver={(e) => handleDragOver(e, column.accessor as keyof T)}
                onDrop={(e) => handleDrop(e, column.accessor as keyof T)}
                onDragEnd={handleDragEnd}
                testId={`${setTestIdPrefix()}table-head-cell-${column.accessor.toString()}`}
                hidden={!column.visible}
                isSortable={column.sort}
                sortDirection={column.sorted || false}
                key={column.accessor.toString()}
                onClick={() => {
                  if (!column.sort) { return; }
                  handleSort(column.accessor as keyof T);
                  setCurrentPage(1);
                }}>
                {isFunction(column.header) ? column.header(column) : column.header}
              </TableHeadCell>
            ))}
            {partitionedColumns.normalColumns.map((column) => (
              <TableHeadCell
                draggable
                onDragStart={(e) => handleDragStart(e, column.accessor as keyof T)}
                onDragOver={(e) => handleDragOver(e, column.accessor as keyof T)}
                onDrop={(e) => handleDrop(e, column.accessor as keyof T)}
                onDragEnd={handleDragEnd}
                testId={`${setTestIdPrefix()}table-head-cell-${column.accessor.toString()}`}
                hidden={!column.visible}
                isSortable={column.sort}
                sortDirection={column.sorted || false}
                key={column.accessor.toString()}
                onClick={() => {
                  if (!column.sort) { return; }
                  handleSort(column.accessor as keyof T);
                  setCurrentPage(1);
                }}>
                {isFunction(column.header) ? column.header(column) : column.header}
              </TableHeadCell>
            ))}
            {partitionedColumns.rightColumns.map((column) => (
              <TableHeadCell
                draggable
                onDragStart={(e) => handleDragStart(e, column.accessor as keyof T)}
                onDragOver={(e) => handleDragOver(e, column.accessor as keyof T)}
                onDrop={(e) => handleDrop(e, column.accessor as keyof T)}
                onDragEnd={handleDragEnd}
                testId={`${setTestIdPrefix()}table-head-cell-${column.accessor.toString()}`}
                hidden={!column.visible}
                isSortable={column.sort}
                sortDirection={column.sorted || false}
                key={column.accessor.toString()}
                onClick={() => {
                  if (!column.sort) { return; }
                  handleSort(column.accessor as keyof T);
                  setCurrentPage(1);
                }}>
                {isFunction(column.header) ? column.header(column) : column.header}
              </TableHeadCell>
            ))}
          </TableRow>

          { !loading && !data.length && (<TableRow>
            <TableCell colSpan={checkbox ? visibleColumns.length + 1 : visibleColumns.length}>
              {t('common.noResultsFound')}
            </TableCell>
          </TableRow>)}

          { loading && Array.from({ length: linesPerPage }, (_, index: number) => (
            <TableRow key={index} testId={`${setTestIdPrefix()}table-row-loading`}>
              {Array.from({ length: checkbox ? visibleColumns.length + 1 : visibleColumns.length }, (__, indexCel: number) => (
                <TableCell key={`cell-${indexCel}`} testId={`${setTestIdPrefix()}table-cell-loading`}>
                  <Skeleton width='100%' />
                </TableCell>
              ))}
            </TableRow>
          ))}

          { !loading && data.length > 0 ? data.map((row, index) => (
            <TableRow
              testId={`${setTestIdPrefix()}table-row-content-${'id' in row ? row.id : ''}`}
              key={getRowKey(row, index)} onClick={() => {
                if (onTableRowClick) {
                  onTableRowClick(row);
                }
              }}>
              { checkbox && <TableCell padding='none'>
                <Box display='flex' alignItems='center' justifyContent='center'>
                  <Checkbox
                    testId={`${setTestIdPrefix()}table-checkbox-${'id' in row ? row.id : ''}`}
                    checked={isSelected(row)}
                    onClick={(event) => { event?.stopPropagation(); }}
                    onChange={() => toggle(row)}
                    customStyle={{
                      margin: 0,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                </Box>
              </TableCell> }
              {partitionedColumns.leftColumns.map((column) => {
                const ColumnTableCell = column.CustomTableCell || TableCell;
                return (
                  <ColumnTableCell
                    testId={`${setTestIdPrefix()}table-cell-${column.accessor.toString()}-${'id' in row ? row.id : ''}`}
                    hidden={!column.visible} key={column.accessor.toString()}>
                    {isFunction(column.cell) ? column.cell(row, index) : row[column.accessor]}
                  </ColumnTableCell>
                );
              })}
              {partitionedColumns.normalColumns.map((column) => {
                const ColumnTableCell = column.CustomTableCell || TableCell;
                return (
                  <ColumnTableCell
                    testId={`${setTestIdPrefix()}table-cell-${column.accessor.toString()}-${'id' in row ? row.id : ''}`}
                    hidden={!column.visible} key={column.accessor.toString()}>
                    {isFunction(column.cell) ? column.cell(row, index) : row[column.accessor]}
                  </ColumnTableCell>
                );
              })}
              {partitionedColumns.rightColumns.map((column) => {
                const ColumnTableCell = column.CustomTableCell || TableCell;
                return (
                  <ColumnTableCell
                    testId={`${setTestIdPrefix()}table-cell-${column.accessor.toString()}-${'id' in row ? row.id : ''}`}
                    hidden={!column.visible} key={column.accessor.toString()}>
                    {isFunction(column.cell) ? column.cell(row, index) : row[column.accessor]}
                  </ColumnTableCell>
                );
              })}
            </TableRow>
          )) : <></>}
          { footer && <>
            <TableRow>
              {partitionedColumns.leftColumns.map((column) => (
                <TableCell hidden={!column.visible} key={`footer-${column.accessor.toString()}`}>
                  {isFunction(footer) ? footer(data) : ''}
                </TableCell>
              ))}
              {partitionedColumns.normalColumns.map((column) => (
                <TableCell hidden={!column.visible} key={`footer-${column.accessor.toString()}`}>
                  {isFunction(footer) ? footer(data) : ''}
                </TableCell>
              ))}
              {partitionedColumns.rightColumns.map((column) => (
                <TableCell hidden={!column.visible} key={`footer-${column.accessor.toString()}`}>
                  {isFunction(footer) ? footer(data) : ''}
                </TableCell>
              ))}
            </TableRow>
          </> }
        </Table>
      </Box>

      { pagination && (
        <Pagination
          page={currentPage}
          count={Math.ceil(totalLines / itemsPerPage)}
          perPage={itemsPerPage}
          total={totalLines}
          onChange={(_, newPage) => setCurrentPage(newPage)}
          onChangePerPage={setItemsPerPage}
          showPerPage={true}
          sx={{
            p: 1,
            textAlign: 'right',
            '.MuiPagination-ul': { justifyContent: 'end' },
          }}
        />
      )}
    </>
  );
};

export default RebalanceReportTable;
